import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type {
  FieldPath,
  UseFormSetValue,
  UseFormGetValues,
} from 'react-hook-form';
import {
  constSelector,
  RelayEnvironmentScope,
  useCherreValue,
} from '@cherre-frontend/data-fetching';
import { $getMappingFieldCustomAttributes } from '../services/dsp/useGetMappingFieldCustomAttributes';
import { type MappingFieldsTableValues } from 'src/products/models-library/schema';
import { MappingTableAutocompleteField } from './MappingTableAutocompleteField';

type CustomAttribute = { label: string; slug: string };
type MappingField = { custom_attributes: null | CustomAttribute[] };
type GetMappingFieldsResponse = undefined | Array<MappingField>;

type MappingLookupColumnAutocompleteProps = {
  disabled?: boolean;
  mappingFieldName?: string;
  fieldName: FieldPath<MappingFieldsTableValues>;
  setMappingValues: UseFormSetValue<MappingFieldsTableValues>;
  getMappingValues: UseFormGetValues<MappingFieldsTableValues>;
};

const defaultLookupColumnOptions = [
  { label: 'Source Mapping Label', value: 'source_mapping_label' },
  { label: 'Target Mapping Code', value: 'target_mapping_code' },
  { label: 'Target Mapping Label', value: 'target_mapping_label' },
];

const MappingLookupColumnAutocomplete = ({
  fieldName,
  getMappingValues,
  setMappingValues,
  disabled,
  mappingFieldName,
}: MappingLookupColumnAutocompleteProps) => {
  const organizationId =
    useSelector((state) => state.user.profile.value.organizationId) ?? -1;
  const getMappingSetsSelector = mappingFieldName
    ? $getMappingFieldCustomAttributes({ organizationId, mappingFieldName })
    : constSelector([]);

  const getMappingFields = useCherreValue(getMappingSetsSelector);
  const mappingFields = getMappingFields as unknown as GetMappingFieldsResponse;

  const autoCompleteOptions = useMemo(() => {
    const customAttributes = mappingFields?.flatMap(({ custom_attributes }) => {
      return (custom_attributes || []).map(({ label, slug: value }) => {
        return { label, value };
      });
    });
    return [...defaultLookupColumnOptions, ...(customAttributes || [])];
  }, [mappingFields]);

  return (
    <MappingTableAutocompleteField
      name={fieldName}
      getMappingValues={getMappingValues}
      setMappingValues={setMappingValues}
      label='Select Lookup Column'
      options={autoCompleteOptions}
      disabled={disabled}
    />
  );
};

const MappingLookupColumnAutocompleteWrapper = (
  props: MappingLookupColumnAutocompleteProps
) => {
  return (
    <RelayEnvironmentScope environment='view_admin'>
      <MappingLookupColumnAutocomplete {...props} />
    </RelayEnvironmentScope>
  );
};

MappingLookupColumnAutocompleteWrapper.displayName =
  'MappingLookupColumnAutocomplete';
export default MappingLookupColumnAutocompleteWrapper;
